import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';
import {
  Button,
  Flex,
  H3,
  Pagination,
  SelectMenu,
  TextField,
} from '@decisiv/ui-components';
import iconix from '@decisiv/iconix/lib/components';
import Refresh from '@decisiv/iconix/lib/components/Refresh';
import MinusCircle from '@decisiv/iconix/lib/components/MinusCircle';
import PlusCircle from '@decisiv/iconix/lib/components/PlusCircle';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import ExecutionDetailsModal from '../../components/ExecutionDetails/ExecutionDetailsModal';
import {
  FilterTextField,
  HelpInputMessage,
  HelpInputText,
  IntegrationDetailLocations,
  Separator,
} from './style';
import WrapperWithLoading from '../../components/WrapperWithLoading';
import ExecutionsListTable from '../../components/ExecutionsListTable';
import DateTimeRangePicker from '../../components/DateTimeRangePicker';
import DecoratedSelectMenu from '../../components/DecoratedSelectMenu';
import StepFunctionExecutionsApi from '../../api/step_function_executions';
import AttributePicker from './AttributePicker';
import NoExecutionsFound from './NoExecutionsFound';
import useParameterRows from './useParameterRows';

import useRetryExecutionFunctions from '../../hooks/useRetryExecutionFunctions';
import {
  sourceOptions,
  statusOptions,
  triggerOptions,
} from '../../utils/constants';
import Label from '../../components/Label';
import localCache from '../../utils/localCache';
import { STORAGE_KEYS } from '../../utils/storageKeys';
import { dayStartWithHours } from '../../utils/dates';
import {
  buildPayloadSources,
  scalarNodes,
} from '../../utils/payloadSchemaTree';

const NARROWING_FILTERS = [
  'source',
  'triggerType',
  'status',
  'startDate',
  'search',
];

export default function ExecutionsList({ integration }) {
  const itemsPerPage = 25;

  const [currentStartIndex, setCurrentStartIndex] = useState(0);
  const [currentEndIndex, setCurrentEndIndex] = useState(0);
  const [executions, setExecutions] = useState([]);
  const [currentPageExecutions, setCurrentPageExecutions] = useState([]);
  const [totalExecutionsPage, setTotalExecutionsPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedExecution, setSelectedExecution] = useState();
  const [executionModalDetailsVisibility, setExecutionModalDetailsVisibility] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  const {
    isRetryButtonVisible,
    isRetryButtonDisabled,
    handleRetry,
    retryExecutionIdRef,
  } = useRetryExecutionFunctions();
  const [filterSource, setFilterSource] = useState('ALL');
  const [filterTrigger, setFilterTrigger] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const pageChangeHandler = (newPageNumber) => {
    setCurrentPage(newPageNumber);
  };

  function hasCachedFilters() {
    return localCache.get(STORAGE_KEYS.EXECUTIONS_FILTER) !== null;
  }

  const initialSearch = hasCachedFilters()
    ? localCache.get(STORAGE_KEYS.EXECUTIONS_FILTER)
    : {};
  const [filters, setFilters] = useState(initialSearch);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const [searchInput, setSearchInput] = useState(initialSearch.search || '');
  const latestRequest = useRef(0);
  const [selectedDates, setSelectedDates] = useState();
  const DEFAULT_START_HOUR = '00';
  const DEFAULT_END_HOUR = '23';
  const [filterStartHour, setFilterStartHour] = useState(
    initialSearch.startHour || DEFAULT_START_HOUR,
  );
  const [filterEndHour, setFilterEndHour] = useState(
    initialSearch.endHour || DEFAULT_END_HOUR,
  );

  const parameterSources = useMemo(
    () =>
      buildPayloadSources(integration.attributes?.schemas)
        .map((source) => ({ ...source, nodes: scalarNodes(source.nodes) }))
        .filter((source) => source.nodes.length),
    [integration.attributes?.schemas],
  );

  async function getExecutions(customFilters, page = currentPage) {
    latestRequest.current += 1;
    const request = latestRequest.current;
    setLoading(true);

    const responseLogs =
      await StepFunctionExecutionsApi.fetchStepFunctionExecutionsByIntegrationId(
        {
          integrationId: integration.id,
          page,
          source: customFilters?.source,
          triggerType: customFilters?.triggerType,
          status: customFilters?.status,
          startDate: customFilters?.startDate
            ? dayStartWithHours(
                customFilters.startDate,
                customFilters.startHour ?? DEFAULT_START_HOUR,
              )
            : null,
          stopDate: customFilters?.stopDate
            ? dayStartWithHours(
                customFilters.stopDate,
                Number(customFilters.endHour ?? DEFAULT_END_HOUR) + 1,
              )
            : null,
          search: customFilters?.search,
          invocationParameters: customFilters?.invocationParameters,
        },
      );
    // A slower earlier search must not overwrite the one the filters now show.
    if (request !== latestRequest.current) return;

    if (responseLogs.data) {
      setErrorMessage(null);
      setExecutions(responseLogs.data);

      const pageExecutions = responseLogs.data?.slice(0, 25);
      const startIndex = (page - 1) * itemsPerPage;
      setCurrentStartIndex(startIndex);

      const endIndex = startIndex + pageExecutions.length;
      setCurrentEndIndex(endIndex);
      setTotalRecords(responseLogs.totalRecords);
      setTotalExecutionsPage(
        Math.ceil(responseLogs.totalRecords / itemsPerPage),
      );
      setCurrentPageExecutions(pageExecutions);
    } else {
      setExecutions([]);
      setCurrentPageExecutions([]);
      setTotalRecords(0);
      // A 5xx detail is the raw exception message, not something to show.
      const [error] = responseLogs.errors ?? [];
      setErrorMessage(
        (Number(error?.status) < 500 && error.detail) ||
          t`Something went wrong, please try again.`,
      );
    }
    setLoading(false);
  }

  // A filter change always restarts at page 1: the result set changes size, so
  // the previous offset can land past the end and render an empty page. The
  // fetch is left to the effect below, which keys on the filter state — the
  // cache write is only a convenience for the next visit and may silently
  // no-op (Safari private mode), so it must never be what a refetch reads.
  // Parameter filters stay out of the cache: they are paths of this
  // integration's payload, and the cache is shared by every integration.
  const applyFilters = useCallback((updatedFilter) => {
    setFilters(updatedFilter);
    localCache.set(
      STORAGE_KEYS.EXECUTIONS_FILTER,
      omit(updatedFilter, 'invocationParameters'),
    );
    setCurrentPage(1);
  }, []);

  const applySearch = useMemo(
    () =>
      debounce((value) => {
        const search = value.trim() || null;
        if (search === (filtersRef.current.search ?? null)) return;
        applyFilters({ ...filtersRef.current, search });
      }, 500),
    [applyFilters],
  );

  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const parameters = useParameterRows((invocationParameters) =>
    applyFilters({ ...filters, invocationParameters }),
  );

  const applyParametersOnEnter = (e) => {
    if (e.key === 'Enter') parameters.apply();
  };

  const clearFilters = () => {
    applySearch.cancel();
    localCache.remove(STORAGE_KEYS.EXECUTIONS_FILTER);
    setFilters({});

    // Reset the inputs
    setFilterSource('ALL');
    setFilterTrigger('ALL');
    setFilterStatus('ALL');
    setSelectedDates([undefined, undefined]);
    setFilterStartHour(DEFAULT_START_HOUR);
    setFilterEndHour(DEFAULT_END_HOUR);
    setSearchInput('');
    parameters.reset();

    // The original passed the page number where the filters go, which read as
    // "no filters" by accident and left the offset on whatever page was open.
    // setFilters({}) above plus this land in one batch, so the fetch effect
    // runs once with the cleared filters on page 1.
    setCurrentPage(1);
  };

  // Restore the inputs from the cached filter set, once, on mount.
  useEffect(() => {
    const filter = initialSearch;

    setFilterSource(filter.source == null ? 'ALL' : filter.source);
    setFilterTrigger(filter.triggerType == null ? 'ALL' : filter.triggerType);
    setFilterStatus(filter.status == null ? 'ALL' : filter.status);
    setSelectedDates(
      filter.startDate == null
        ? [undefined, undefined]
        : [new Date(filter.startDate), new Date(filter.stopDate)],
    );
    setFilterStartHour(filter.startHour || DEFAULT_START_HOUR);
    setFilterEndHour(filter.endHour || DEFAULT_END_HOUR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The automatic fetch path; Refresh and the post-retry callback fetch directly.
  // Reads `filters`, never the cache, so a failed cache write can't serve stale
  // results.
  useEffect(() => {
    if (!integration?.id) return;
    getExecutions(filters, currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration?.id, currentPage, filters]);

  const handleChangeSource = (e) => {
    setFilterSource(e);

    let updatedFilter;

    if (e.length === 0 || e.includes('ALL')) {
      updatedFilter = {
        ...filters,
        source: null,
      };
    } else {
      updatedFilter = {
        ...filters,
        source: e,
      };
    }

    applyFilters(updatedFilter);
  };

  const handleChangeTrigger = (e) => {
    setFilterTrigger(e);

    let updatedFilter;

    if (e.length === 0 || e.includes('ALL')) {
      updatedFilter = {
        ...filters,
        triggerType: null,
      };
    } else {
      updatedFilter = {
        ...filters,
        triggerType: e,
      };
    }

    applyFilters(updatedFilter);
  };

  const handleChangeStatus = (e) => {
    setFilterStatus(e);

    let updatedFilter;

    if (e.length === 0 || e.includes('ALL')) {
      updatedFilter = {
        ...filters,
        status: null,
      };
    } else {
      updatedFilter = {
        ...filters,
        status: e,
      };
    }

    applyFilters(updatedFilter);
  };

  const applyDateTimeRange = (dates, startHour, endHour) => {
    setFilterStartHour(startHour);
    setFilterEndHour(endHour);

    const hasRange = dates && dates[0] && dates[1];
    setSelectedDates(hasRange ? dates : [undefined, undefined]);

    const updatedFilter = {
      ...filters,
      startDate: hasRange ? dates[0] : null,
      stopDate: hasRange ? dates[1] : null,
      startHour,
      endHour,
    };

    applyFilters(updatedFilter);
  };

  const clearDateTimeRange = () => {
    setSelectedDates([undefined, undefined]);
    setFilterStartHour(DEFAULT_START_HOUR);
    setFilterEndHour(DEFAULT_END_HOUR);

    const updatedFilter = {
      ...filters,
      startDate: null,
      stopDate: null,
      startHour: DEFAULT_START_HOUR,
      endHour: DEFAULT_END_HOUR,
    };

    applyFilters(updatedFilter);
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    applySearch(e.target.value);
  };

  const handleRefresh = () => {
    getExecutions(filters, currentPage);
  };

  const filtered =
    NARROWING_FILTERS.some((key) => filters[key] != null) ||
    !isEmpty(filters.invocationParameters);

  return (
    <IntegrationDetailLocations
      textAlign="center"
      name="integrationDetailLocations"
    >
      <ExecutionDetailsModal
        log={selectedExecution}
        visibility={executionModalDetailsVisibility}
        handleVisibility={setExecutionModalDetailsVisibility}
        modalTitle={t`Execution Details`}
        logs={executions}
        onRefreshLogs={handleRefresh}
      />

      <Flex flexDirection="column" name="locationResult" flexGrow={1}>
        <Flex name="searchBar">
          <Flex
            marginTop={2}
            marginLeft={2}
            marginRight={2}
            name="topBar"
            flexGrow={1}
          >
            <Flex name="searchBox">
              <Flex>
                <H3 marginRight={2} marginTop={0.2}>
                  <Trans>Executions</Trans>
                </H3>
                <Separator />
              </Flex>
              <Flex flexDirection="column">
                <FilterTextField
                  width={32}
                  icon={iconix.Search}
                  label={t`Search executions by location`}
                  hideLabel
                  value={searchInput}
                  onChange={handleSearchChange}
                />
                <HelpInputMessage alignItems="center">
                  <iconix.ExclamationCircleF />
                  <HelpInputText shade={1}>
                    <Trans>Search by SRM Account ID, name or city</Trans>
                  </HelpInputText>
                </HelpInputMessage>
              </Flex>
            </Flex>
            <Flex justifyContent="flex-end" flexGrow={1}>
              <Button
                icon={Refresh}
                text={t`Refresh`}
                size="small"
                kind="secondary"
                onClick={handleRefresh}
              />
            </Flex>
          </Flex>
        </Flex>
        <Flex flexDirection="row" name="filters" marginLeft={2} marginTop={2}>
          <Flex>
            <SelectMenu
              label={t`Source:`}
              name="select-source"
              options={sourceOptions}
              value={filterSource}
              selected={filterSource}
              onChange={handleChangeSource}
              zIndex={999}
            />
          </Flex>
          <Flex marginLeft={2}>
            <SelectMenu
              label={t`Trigger:`}
              name="select-trigger"
              options={triggerOptions}
              value={filterTrigger}
              selected={filterTrigger}
              onChange={handleChangeTrigger}
              zIndex={999}
            />
          </Flex>
          <Flex marginLeft={2}>
            <DecoratedSelectMenu
              label={t`Status:`}
              name="select-status"
              options={statusOptions}
              value={filterStatus}
              selected={filterStatus}
              onChange={handleChangeStatus}
              zIndex={999}
            />
          </Flex>
          <Flex marginLeft={2}>
            <Label shade={1}>{t`Date Range`}:</Label>
          </Flex>
          <Flex marginLeft={2}>
            <DateTimeRangePicker
              name="executions-date-range"
              values={selectedDates}
              startHour={filterStartHour}
              endHour={filterEndHour}
              onApply={applyDateTimeRange}
              onClear={clearDateTimeRange}
              zIndex={999}
            />
          </Flex>
          <Flex marginLeft={2} marginTop={0.5}>
            <Button
              text={t`Clear All`}
              size="small"
              variant="ghost"
              onClick={clearFilters}
            />
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          alignItems="flex-start"
          name="parameterFilters"
          marginLeft={2}
          marginTop={1}
        >
          {parameters.rows.map((row, index) => (
            <Flex key={row.id} alignItems="flex-start" marginTop={1}>
              <Button
                aria-label={t`Remove parameter filter`}
                variant="ghost"
                icon={MinusCircle}
                onClick={() => parameters.remove(row.id)}
              />
              <Flex style={{ width: '18.75rem' }} marginLeft={1}>
                <AttributePicker
                  label={t`parameter`}
                  name={`parameter-path-${index}`}
                  sources={parameterSources}
                  value={row.path}
                  onChange={(e) =>
                    parameters.update(row.id, { path: e.target.value })
                  }
                  onSelect={(path) => parameters.update(row.id, { path })}
                  onKeyDown={applyParametersOnEnter}
                  warningMessage={parameters.warningFor(row.path)}
                  leavesOnly
                  zIndex={999}
                />
              </Flex>
              <Flex style={{ width: '18.75rem' }} marginLeft={1}>
                <TextField
                  label={t`value`}
                  name={`parameter-value-${index}`}
                  value={row.value}
                  onChange={(e) =>
                    parameters.update(row.id, { value: e.target.value })
                  }
                  onKeyDown={applyParametersOnEnter}
                />
              </Flex>
            </Flex>
          ))}
          <Flex marginTop={1}>
            <Button
              text={t`Add parameter filter`}
              icon={PlusCircle}
              size="small"
              kind="secondary"
              onClick={parameters.add}
            />
            {parameters.rows.length > 0 && (
              <Flex marginLeft={1}>
                <Button
                  text={t`Apply parameters`}
                  size="small"
                  disabled={!parameters.canApply}
                  onClick={parameters.apply}
                />
              </Flex>
            )}
          </Flex>
          {parameters.rows.length > 0 && (
            <HelpInputMessage alignItems="center" marginTop={0.5}>
              <iconix.ExclamationCircleF />
              <HelpInputText shade={1}>
                <Trans>
                  Exact match on the invocation payload. On busy integrations,
                  narrow the date range first.
                </Trans>
              </HelpInputText>
            </HelpInputMessage>
          )}
        </Flex>
        <Flex name="searchResult">
          <WrapperWithLoading loading={loading}>
            {errorMessage || totalRecords === 0 ? (
              <Flex flexGrow={1} flexDirection="column" alignItems="center">
                <NoExecutionsFound error={errorMessage} filtered={filtered} />
                {errorMessage && currentPage > 1 && (
                  <Pagination
                    totalPages={totalExecutionsPage}
                    onPageChange={pageChangeHandler}
                    activePage={currentPage}
                  />
                )}
              </Flex>
            ) : (
              <Flex flexGrow={1} flexDirection="column">
                <ExecutionsListTable
                  currentPageExecutions={currentPageExecutions}
                  currentStartIndex={currentStartIndex}
                  currentEndIndex={currentEndIndex}
                  itemsPerPage={itemsPerPage}
                  executions={executions}
                  totalPages={totalExecutionsPage}
                  totalRecords={totalRecords}
                  onPageChange={pageChangeHandler}
                  currentPage={currentPage}
                  setSelectedExecution={setSelectedExecution}
                  isRetryButtonVisible={isRetryButtonVisible}
                  isRetryButtonDisabled={isRetryButtonDisabled}
                  handleRetry={handleRetry}
                  retryExecutionIdRef={retryExecutionIdRef}
                  setSortedExecutions={setCurrentPageExecutions}
                  getExecutions={handleRefresh}
                  setExecutionModalDetailsVisibility={
                    setExecutionModalDetailsVisibility
                  }
                  tableColumns={[
                    'x_decisiv_trace_id',
                    'location_name',
                    'city',
                    'http_status',
                    'source',
                    'trigger_type',
                    'duration',
                    'started',
                    'end_time',
                    'actions',
                  ]}
                  maxRetryCount={integration.attributes.max_retries}
                />
              </Flex>
            )}
          </WrapperWithLoading>
        </Flex>
      </Flex>
    </IntegrationDetailLocations>
  );
}

ExecutionsList.propTypes = {
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
};
